
import React from 'react';
import { Employee } from '@/utils/mockData';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Mail, Phone, MoreHorizontal, CalendarRange, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';

interface EmployeeCardProps {
  employee: Employee;
  onEdit: (employee: Employee) => void;
  onDelete: (id: string) => void;
  onViewSchedule: (id: string) => void;
}

const EmployeeCard: React.FC<EmployeeCardProps> = ({ 
  employee, 
  onEdit, 
  onDelete, 
  onViewSchedule 
}) => {
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase();
  };

  const getDepartmentColor = (department: string) => {
    const colors: Record<string, string> = {
      'Sales': 'bg-blue-100 text-blue-800 border-blue-200',
      'Customer Service': 'bg-green-100 text-green-800 border-green-200',
      'Management': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'Kitchen': 'bg-amber-100 text-amber-800 border-amber-200',
      'Delivery': 'bg-pink-100 text-pink-800 border-pink-200',
    };
    
    return colors[department] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Functie om verwijderen te triggeren met expliciete logging
  const handleDelete = () => {
    console.log("Verwijderen aangeroepen voor medewerker:", employee.id);
    onDelete(employee.id);
  };

  // Functie om bewerken te triggeren met logging
  const handleEdit = () => {
    console.log("Bewerken aangeroepen voor medewerker:", employee.id);
    onEdit(employee);
  };

  // Functie om rooster bekijken te triggeren met logging
  const handleViewSchedule = () => {
    console.log("Rooster bekijken aangeroepen voor medewerker:", employee.id);
    onViewSchedule(employee.id);
  };

  return (
    <Card className="overflow-hidden transition-all hover:shadow-md">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <Avatar className="h-12 w-12">
            <AvatarImage src={employee.avatar || '/placeholder.svg'} alt={employee.name} />
            <AvatarFallback>{getInitials(employee.name)}</AvatarFallback>
          </Avatar>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Acties</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleEdit}>
                Bewerken
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleViewSchedule}>
                Rooster bekijken
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleDelete}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Verwijderen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        <h3 className="text-lg font-medium">{employee.name}</h3>
        <div className="flex items-center mt-1 mb-3">
          <Badge 
            variant="outline" 
            className={getDepartmentColor(employee.department)}
          >
            {employee.department}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground mb-1">{employee.position}</p>
        
        <div className="mt-4 space-y-2">
          <div className="flex items-center text-sm">
            <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
            <span className="text-muted-foreground">{employee.email}</span>
          </div>
          <div className="flex items-center text-sm">
            <Phone className="h-4 w-4 mr-2 text-muted-foreground" />
            <span className="text-muted-foreground">{employee.phone}</span>
          </div>
        </div>
      </CardContent>
      
      <CardFooter className="px-6 py-4 bg-muted/30 border-t flex justify-between">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleEdit}
        >
          Bewerken
        </Button>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            size="sm"
            onClick={handleViewSchedule}
            className="flex items-center gap-1"
          >
            <CalendarRange className="h-4 w-4" />
            Rooster
          </Button>
          <Button 
            variant="destructive"
            size="sm"
            onClick={handleDelete}
            className="flex items-center gap-1"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

export default EmployeeCard;
